import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import authService from '../../../modules/auth/services/authService';
import { menuService } from '../../../modules/menu/services/menuService';
import PreferencesWizard from '../../../modules/menu/components/PreferencesWizard';
import Logo from '../Logo';
import LanguageSwitcher from '../LanguageSwitcher';

const Navbar = ({ showAuthLinks = true }) => {
    const { t } = useTranslation();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuth = () => {
            const auth = authService.isAuthenticated();
            setIsAuthenticated(auth);

            if (auth) {
                // Obtener datos del usuario desde localStorage
                const userDataStr = localStorage.getItem('user_data');
                if (userDataStr) {
                    try {
                        const userData = JSON.parse(userDataStr);
                        setUserEmail(userData.email || 'Usuario');
                    } catch (error) {
                        setUserEmail('Usuario');
                    }
                } else {
                    setUserEmail('Usuario');
                }
            }
        };

        checkAuth();
        const interval = setInterval(checkAuth, 1000);
        return () => clearInterval(interval);
    }, []);

    // Cerrar dropdown al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDropdown && !event.target.closest('.dropdown-container')) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    // Cerrar menú mobile cuando se redimensiona la ventana a desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768 && mobileMenuOpen) {
                setMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [mobileMenuOpen]);

    // Prevenir scroll cuando el menú mobile está abierto
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [mobileMenuOpen]);

    const handleLogout = async () => {
        try {
            await authService.logout();
            authService.logout();
            localStorage.removeItem('user_data');
            setIsAuthenticated(false);
            setUserEmail('');
            setShowDropdown(false);
            setMobileMenuOpen(false);
            navigate('/');
        } catch (error) {
            // Aún así limpiar el token y redirigir
            authService.logout();
            localStorage.removeItem('user_data');
            setIsAuthenticated(false);
            setUserEmail('');
            setShowDropdown(false);
            setMobileMenuOpen(false);
            navigate('/');
        }
    };

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
    };

    const handlePreferencesUpdate = async (preferences) => {
        setIsLoading(true);
        try {
            // Transformar los datos del frontend al formato del backend
            const backendPreferences = {
                num_people: preferences.numPeople,
                allergies: preferences.allergies || [],
                dietary_preferences: preferences.preferences || [],
                budget_level: preferences.budget,
                complexity: preferences.complexity || 'media',
                goal: preferences.goal || 'saludable'
            };

            // Guardar preferencias en el backend
            await menuService.updatePreferences(backendPreferences);

            // Actualizar localStorage
            const userDataStr = localStorage.getItem('user_data');
            if (userDataStr) {
                const userData = JSON.parse(userDataStr);
                userData.preferences = backendPreferences;
                localStorage.setItem('user_data', JSON.stringify(userData));
            }

            setShowPreferencesModal(false);
            // Opcional: mostrar mensaje de éxito
        } catch (error) {
            alert('Error al guardar las preferencias. Inténtalo de nuevo.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        {/* Logo */}
                        <div className="flex items-center">
                            <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
                                <Logo width={120} height={40} textColor="#111827" accentColor="#10b981" />
                            </Link>
                        </div>

                        {/* Desktop Navigation */}
                        {showAuthLinks && <div className="hidden md:flex items-center space-x-4">
                            {isAuthenticated ? (
                                <div className="relative dropdown-container">
                                    <button
                                        onClick={() => setShowDropdown(!showDropdown)}
                                        className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 font-medium transition-colors min-h-[44px] px-2"
                                    >
                                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                                            <span className="text-primary-600 font-semibold text-sm">
                                                {userEmail.charAt(0).toUpperCase()}
                                            </span>
                                        </div>
                                        <span>{userEmail}</span>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {showDropdown && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                                            <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                                                {userEmail}
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setShowPreferencesModal(true);
                                                    setShowDropdown(false);
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px]"
                                            >
                                                ⚙️ {t('navigation.preferences')}
                                            </button>
                                            <Link
                                                to="/plan"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px] flex items-center"
                                                onClick={() => setShowDropdown(false)}
                                            >
                                                📋 {t('navigation.plan')}
                                            </Link>
                                            <Link
                                                to="/now"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px] flex items-center"
                                                onClick={() => setShowDropdown(false)}
                                            >
                                                🕐 {t('navigation.now')}
                                            </Link>
                                            <Link
                                                to="/shopping-list"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px] flex items-center"
                                                onClick={() => setShowDropdown(false)}
                                            >
                                                🛒 {t('navigation.shoppingList')}
                                            </Link>
                                            <Link
                                                to="/recipes"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px] flex items-center"
                                                onClick={() => setShowDropdown(false)}
                                            >
                                                📚 {t('navigation.recipes')}
                                            </Link>
                                            <button
                                                onClick={handleLogout}
                                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors min-h-[44px]"
                                            >
                                                🚪 {t('navigation.logout')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <Link
                                        to="/pricing"
                                        className="text-gray-700 hover:text-gray-900 font-medium transition-colors min-h-[44px] flex items-center px-3"
                                    >
                                        {t('navigation.pricing')}
                                    </Link>
                                    <Link
                                        to="/login"
                                        className="text-primary-600 hover:text-primary-700 font-medium transition-colors min-h-[44px] flex items-center px-3"
                                    >
                                        {t('navigation.login')}
                                    </Link>
                                    <Link
                                        to="/register"
                                        className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors min-h-[44px] flex items-center"
                                    >
                                        {t('navigation.register')}
                                    </Link>
                                </>
                            )}
                        </div>}

                        {/* Mobile menu button */}
                        {showAuthLinks && <div className="md:hidden">
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                aria-label={t('navigation.mainMenu')}
                            >
                                {mobileMenuOpen ? (
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                ) : (
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                )}
                            </button>
                        </div>}
                    </div>
                </div>

                {/* Mobile menu overlay */}
                {mobileMenuOpen && (
                    <div className="fixed inset-0 z-50 md:hidden">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                            onClick={closeMobileMenu}
                        ></div>

                        {/* Menu panel */}
                        <div className="fixed inset-y-0 right-0 max-w-xs w-full bg-white shadow-xl">
                            <div className="flex flex-col h-full">
                                {/* Header */}
                                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                                    <span className="text-lg font-semibold text-gray-900">{t('navigation.menu')}</span>
                                    <button
                                        onClick={closeMobileMenu}
                                        className="p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
                                        aria-label={t('navigation.closeMenu')}
                                    >
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Language Switcher */}
                                {/*<div className="p-4 border-b border-gray-200">
                                    <LanguageSwitcher />
                                </div>*/}

                                {/* Menu items */}
                                <div className="flex-1 overflow-y-auto">
                                    {isAuthenticated ? (
                                        <div className="py-2">
                                            {/* User info */}
                                            <div className="px-4 py-3 border-b border-gray-100">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                                                        <span className="text-primary-600 font-semibold">
                                                            {userEmail.charAt(0).toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-gray-900 truncate">{userEmail}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Navigation links */}
                                            <div className="px-2 py-2 space-y-1">
                                                <button
                                                    onClick={() => {
                                                        setShowPreferencesModal(true);
                                                        closeMobileMenu();
                                                    }}
                                                    className="w-full text-left px-4 py-3 text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3"
                                                >
                                                    <span className="text-xl">⚙️</span>
                                                    <span>{t('navigation.preferences')}</span>
                                                </button>
                                                <Link
                                                    to="/plan"
                                                    className="block px-4 py-3 text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3"
                                                    onClick={closeMobileMenu}
                                                >
                                                    <span className="text-xl">📋</span>
                                                    <span>{t('navigation.plan')}</span>
                                                </Link>
                                                <Link
                                                    to="/now"
                                                    className="block px-4 py-3 text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3"
                                                    onClick={closeMobileMenu}
                                                >
                                                    <span className="text-xl">🕐</span>
                                                    <span>{t('navigation.now')}</span>
                                                </Link>
                                                <Link
                                                    to="/shopping-list"
                                                    className="block px-4 py-3 text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3"
                                                    onClick={closeMobileMenu}
                                                >
                                                    <span className="text-xl">🛒</span>
                                                    <span>{t('navigation.shoppingList')}</span>
                                                </Link>
                                                <Link
                                                    to="/recipes"
                                                    className="block px-4 py-3 text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3"
                                                    onClick={closeMobileMenu}
                                                >
                                                    <span className="text-xl">📚</span>
                                                    <span>{t('navigation.recipes')}</span>
                                                </Link>
                                            </div>

                                            {/* Logout button */}
                                            <div className="px-2 py-2 border-t border-gray-200 mt-2">
                                                <button
                                                    onClick={handleLogout}
                                                    className="w-full text-left px-4 py-3 text-base text-red-600 hover:bg-red-50 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3"
                                                >
                                                    <span className="text-xl">🚪</span>
                                                    <span>{t('navigation.logout')}</span>
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="px-2 py-2 space-y-1">
                                            <Link
                                                to="/pricing"
                                                className="block px-4 py-3 text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3"
                                                onClick={closeMobileMenu}
                                            >
                                                <span className="text-xl">💰</span>
                                                <span>{t('navigation.pricing')}</span>
                                            </Link>
                                            <Link
                                                to="/login"
                                                className="block px-4 py-3 text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3"
                                                onClick={closeMobileMenu}
                                            >
                                                <span className="text-xl">🔑</span>
                                                <span>{t('navigation.login')}</span>
                                            </Link>
                                            <Link
                                                to="/register"
                                                className="block px-4 py-3 text-base text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors min-h-[56px] flex items-center space-x-3 font-medium"
                                                onClick={closeMobileMenu}
                                            >
                                                <span className="text-xl">✨</span>
                                                <span>{t('navigation.register')}</span>
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </nav>

            {/* Modal de Preferencias */}
            {showPreferencesModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
                    {/* Botón de cerrar */}
                    <button
                        onClick={() => setShowPreferencesModal(false)}
                        className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    {/* Wizard de Preferencias a pantalla completa */}
                    <PreferencesWizard
                        onMenuGenerated={handlePreferencesUpdate}
                        onStartMenuGeneration={() => { }} // No necesario en este contexto
                        setLoading={setIsLoading}
                        loadUserPreferences={true}
                    />
                </div>
            )}
        </>
    );
};

export default Navbar;
